import { useState, useEffect } from "react";
import { Search, ArrowLeft, X, ExternalLink, ThumbsUp, ThumbsDown, Send } from "lucide-react";
import helpcenterIcon from "@/assets/helpcenter-icon.ico";
import agentIcon from "@/assets/agent-icon.ico";
import userManagementScreenshot from "@/assets/user-management-screenshot.png";
type View = "home" | "search" | "article" | "agent";
const InteractiveHelpCenter = () => {
  const [currentView, setCurrentView] = useState<View>("home");
  const [selectedArticle, setSelectedArticle] = useState<number | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showQuestion, setShowQuestion] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [questionLines, setQuestionLines] = useState<string[]>([]);
  const [answerLines, setAnswerLines] = useState<string[]>([]);
  const [visibleQuestionLines, setVisibleQuestionLines] = useState(0);
  const [visibleAnswerLines, setVisibleAnswerLines] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const articles = [{
    id: 1,
    title: "If an auditor has been added but cannot see the data for auditing, what should be done?",
    intro: "If user want to add an auditor to the system, user can do so as follows:",
    steps: ["Go to the User Management page.", "Go to the Auditor tab.", "Click 'Add Auditor' button and fill in the required information."]
  }, {
    id: 2,
    title: "How to add an Auditor",
    intro: "If user want to add an auditor to the system, user can do so as follows:",
    steps: ["Go to the User Management page.", "Go to the Auditor tab.", "Click 'Add Auditor' button and fill in the required information."]
  }, {
    id: 3,
    title: "Starting the Verification Process",
    intro: "If user want to add an auditor to the system, user can do so as follows:",
    steps: ["Go to the User Management page.", "Go to the Auditor tab.", "Click 'Add Auditor' button and fill in the required information."]
  }];
  const question = "We have 5 air-conditioning units, but only 2 refrigerants were refilled last year. How should we record refrigerant emissions, and what evidence is required for auditor declaration?";
  const answer = "In this case, refrigerant should be reported as leakage during operation, not as full system replacement. Since only 2 units were refilled, we recommend calculating emissions based on a leakage proportion rather than assuming all 5 units leaked.";
  const handleTalkToAgent = () => {
    setCurrentView("agent");
    setIsConnecting(true);
    setShowQuestion(false);
    setShowAnswer(false);
    setShowFeedback(false);
    setVisibleQuestionLines(0);
    setVisibleAnswerLines(0);
    const qLines = question.split(/(?<=[.?])\s+/);
    const aLines = answer.split(/(?<=[.?])\s+/);
    setQuestionLines(qLines);
    setAnswerLines(aLines);
    setTimeout(() => {
      setIsConnecting(false);
      setShowQuestion(true);
    }, 2000);
  };
  useEffect(() => {
    if (showQuestion && visibleQuestionLines < questionLines.length) {
      const timer = setTimeout(() => {
        setVisibleQuestionLines(prev => prev + 1);
      }, 400);
      return () => clearTimeout(timer);
    } else if (showQuestion && visibleQuestionLines === questionLines.length && !showAnswer) {
      const timer = setTimeout(() => {
        setShowAnswer(true);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [showQuestion, visibleQuestionLines, questionLines.length, showAnswer]);
  useEffect(() => {
    if (showAnswer && visibleAnswerLines < answerLines.length) {
      const timer = setTimeout(() => {
        setVisibleAnswerLines(prev => prev + 1);
      }, 400);
      return () => clearTimeout(timer);
    } else if (showAnswer && visibleAnswerLines === answerLines.length && !showFeedback) {
      const timer = setTimeout(() => {
        setShowFeedback(true);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [showAnswer, visibleAnswerLines, answerLines.length, showFeedback]);
  const handleBack = () => {
    if (currentView === "search" && selectedArticle !== null) {
      setSelectedArticle(null);
    } else {
      setCurrentView("home");
      setSelectedArticle(null);
      setIsConnecting(false);
      setShowQuestion(false);
      setShowAnswer(false);
      setShowFeedback(false);
    }
  };
  const handleClose = () => {
    setCurrentView("home");
    setSelectedArticle(null);
    setIsConnecting(false);
    setShowQuestion(false);
    setShowAnswer(false);
    setShowFeedback(false);
  };
  return <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 w-full max-w-[380px] h-[500px] sm:h-[620px] flex flex-col mx-auto">
      {/* Header */}
      <div className="bg-[#2BB673] px-4 py-3 flex items-center justify-between flex-shrink-0">
        {currentView === "home" ? <div className="flex items-center gap-2">
            
            <h3 className="text-white font-semibold text-base">
              Carbonwize's Help Center
            </h3>
          </div> : <>
            <button onClick={handleBack} className="flex items-center gap-1.5 text-white hover:opacity-80 transition-opacity text-sm font-medium">
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <button onClick={handleClose} className="text-white hover:opacity-80 transition-opacity">
              <X className="w-5 h-5" />
            </button>
          </>}
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col relative overflow-hidden bg-[#F8FAFB]">
        {/* Home View */}
        {currentView === "home" && <div className="flex-1 flex flex-col items-center justify-center px-6">
            <div className="text-center mb-auto mt-auto flex flex-col items-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4 bg-[#e6f7ef]">
                <span className="text-5xl leading-none">👋</span>
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Hi there!</h2>
              <p className="text-gray-500 text-sm leading-relaxed">
                Welcome to Carbonwize Help Center. How can we assist you today?
              </p>
            </div>
            
            <div className="flex gap-3 mb-8">
              <button onClick={handleTalkToAgent} className="px-4 py-2.5 border border-gray-300 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                Talk to Live Agent
              </button>
              <button onClick={() => setCurrentView("search")} className="px-4 py-2.5 border border-gray-300 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                Search Articles
              </button>
            </div>
          </div>}

        {/* Search View */}
        {currentView === "search" && selectedArticle === null && <div className="flex-1 flex flex-col p-4 overflow-hidden">
            {/* Search Box */}
            <div className="flex gap-2 mb-3">
              <input type="text" defaultValue="auditor" className="flex-1 px-4 py-2.5 text-sm bg-white rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2BB673]/20 focus:border-[#2BB673] transition-all" readOnly />
              <button className="w-10 h-10 bg-[#2BB673] rounded-lg flex items-center justify-center hover:bg-[#249D63] transition-colors">
                <Search className="w-5 h-5 text-white" />
              </button>
            </div>

            <p className="text-[#2BB673] text-sm mb-4">10 articles found</p>

            {/* Article List */}
            <div className="flex-1 overflow-y-auto space-y-2">
              {articles.map((article, index) => <button key={article.id} onClick={() => setSelectedArticle(article.id)} className="w-full text-left p-4 bg-white hover:bg-gray-50 rounded-xl transition-all border border-gray-100 animate-fade-in flex items-start gap-3" style={{
            animationDelay: `${index * 100}ms`
          }}>
                  <svg className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="text-sm text-gray-700 leading-relaxed">
                    {article.title}
                  </span>
                </button>)}
            </div>
          </div>}

        {/* Article Detail View */}
        {currentView === "search" && selectedArticle !== null && <div className="flex-1 flex flex-col overflow-hidden animate-fade-in">
            {(() => {
          const article = articles.find(a => a.id === selectedArticle);
          if (!article) return null;
          return <div className="flex-1 overflow-y-auto p-5">
                  <h3 className="text-[#2BB673] text-lg font-medium mb-4 flex items-center gap-2">
                    {article.title}
                    <ExternalLink className="w-4 h-4" />
                  </h3>
                  
                  {'intro' in article && <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      {article.intro}
                    </p>}

                  {'steps' in article && <div className="space-y-4">
                      {article.steps.map((step, i) => <div key={i}>
                          <p className="text-gray-800 text-sm font-medium mb-2">
                            {i + 1}. {step}
                          </p>
                          {i === 0 && <div className="rounded-lg overflow-hidden border border-gray-200">
                              <img src={userManagementScreenshot} alt="User Management page" className="w-full h-auto object-cover" />
                            </div>}
                        </div>)}
                    </div>}


                  <div className="mt-8 space-y-3">
                    <button className="w-full py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
                      <ThumbsUp className="w-4 h-4" />
                      Helpful
                    </button>
                    <button className="w-full py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
                      <ThumbsDown className="w-4 h-4" />
                      Not helpful
                    </button>
                  </div>
                </div>;
        })()}
          </div>}

        {/* Agent View */}
        {currentView === "agent" && <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto p-4">
              {/* Connecting Animation */}
              {isConnecting && <div className="flex-1 flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="flex items-center gap-1 justify-center mb-2">
                      <div className="w-2 h-2 bg-[#2BB673] rounded-full animate-bounce" style={{
                  animationDelay: "0ms"
                }} />
                      <div className="w-2 h-2 bg-[#2BB673] rounded-full animate-bounce" style={{
                  animationDelay: "150ms"
                }} />
                      <div className="w-2 h-2 bg-[#2BB673] rounded-full animate-bounce" style={{
                  animationDelay: "300ms"
                }} />
                    </div>
                    <p className="text-sm text-gray-500 animate-pulse">Connecting to live agent..</p>
                  </div>
                </div>}

              {/* Chat Messages */}
              {!isConnecting && <div className="space-y-4">
                  {/* User Question */}
                  {showQuestion && <div className="flex justify-end">
                      <div className="bg-gray-100 rounded-2xl rounded-tr-md px-4 py-3 max-w-[85%]">
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {questionLines.slice(0, visibleQuestionLines).join(" ")}
                        </p>
                      </div>
                    </div>}

                  {/* Agent Answer */}
                  {showAnswer && <div className="flex items-start gap-2 animate-fade-in">
                      <div className="w-8 h-8 rounded-full bg-[#E8F5EE] flex items-center justify-center flex-shrink-0 overflow-hidden">
                        <img src={agentIcon} alt="Agent" className="w-5 h-5" />
                      </div>
                      <div className="bg-white rounded-2xl rounded-tl-md px-4 py-3 max-w-[85%] border border-gray-100 shadow-sm">
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {answerLines.slice(0, visibleAnswerLines).join(" ")}
                        </p>
                        {visibleAnswerLines === answerLines.length && <button className="mt-2 text-sm text-[#2BB673] hover:underline font-medium flex items-center gap-1">
                            Learn about calculation
                            <span>→</span>
                          </button>}
                      </div>
                    </div>}

                  {/* Feedback Buttons */}
                  {showFeedback && <div className="flex gap-2 ml-10 animate-fade-in">
                      <button className="px-4 py-1.5 border border-gray-200 rounded-full text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1.5">
                        <ThumbsUp className="w-3 h-3" />
                        Helpful
                      </button>
                      <button className="px-4 py-1.5 border border-gray-200 rounded-full text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1.5">
                        <ThumbsDown className="w-3 h-3" />
                        Not helpful
                      </button>
                    </div>}
                </div>}
            </div>

            {/* Input Field */}
            {showFeedback && <div className="p-4 border-t border-gray-100 bg-white animate-fade-in">
                <div className="flex gap-2">
                  <input type="text" placeholder="Ask me about carbon accounting" className="flex-1 px-4 py-2.5 text-sm bg-gray-50 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#2BB673]/20 focus:border-[#2BB673] transition-all" readOnly />
                  <button className="w-10 h-10 bg-[#2BB673] rounded-full flex items-center justify-center hover:bg-[#249D63] transition-colors">
                    <Send className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>}
          </div>}
      </div>
    </div>;
};
export default InteractiveHelpCenter;